import React, { type PropsWithChildren, useMemo } from 'react'

import { useIdentity } from '@vp/ubik-context'

import { getCanonicalIdFromAuthHeader } from '../../../utils/jwt'

export type AuthorizationContext = {
  authorizationHeader?: string
  shopperId?: string
}

export const AuthorizationProviderContext = React.createContext<AuthorizationContext | undefined>(undefined)

export function useAuthorization() {
  const ctx = React.useContext(AuthorizationProviderContext)
  if (!ctx) {
    throw new Error('useAuthorization must be used within an AuthorizationProvider')
  }
  return ctx
}

export function AuthorizationProvider(props: PropsWithChildren<{ authorizationHeader?: string }>) {
  const identityCtx = useIdentity()

  // Usually we'll get the auth token from the identity context, but for local development, the auth token can be passed
  // in the server-side data.
  const authorizationHeader = props.authorizationHeader ?? identityCtx?.identity?.authorizationHeader
  // In local development the injected token (props.authorizationHeader) belongs to an authenticated
  // user, while the live browser identity is typically anonymous. Deriving the shopperId from the
  // injected token keeps it consistent with the Authorization header so shopper-scoped calls (e.g.
  // cart reads, which require token principal === shopperId) work locally. In production
  // props.authorizationHeader is undefined, so the live identity's canonical id is used unchanged.
  const shopperId = (props.authorizationHeader && getCanonicalIdFromAuthHeader(props.authorizationHeader)) || identityCtx?.auth?.getCanonicalId() || undefined

  const context = useMemo(
    () => ({
      authorizationHeader,
      shopperId,
    }),
    [authorizationHeader, shopperId],
  )

  return <AuthorizationProviderContext.Provider value={context}>{props.children}</AuthorizationProviderContext.Provider>
}
